/**
 * Integrations page — one place for every official Origin integration.
 *
 * Card grid with status pills; selecting a card mounts that integration's
 * existing settings component below (unchanged components, new home). The
 * FHL Scheduling card hosts the full headless Cal.diy experience (setup,
 * booking pages, meeting types, availability, bookings).
 *
 * Pilot gating: the route/nav are only for users with calSchedulingEnabled
 * or CRM admins (SuperAdmin || Roles includes 'CRM Admin'). Everyone else is
 * redirected back to the CRM dashboard. Admins get the same "view as" picker
 * as the Settings page (effectiveUserId/targetUser pattern).
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  IconButton,
  Tooltip,
  Text,
  useColorModeValue,
  Select as ChakraSelect,
  FormControl,
  FormLabel,
  Icon,
  Badge,
  Spinner,
  Center,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiLink,
  FiCalendar,
  FiPhone,
  FiVideo,
  FiClock,
  FiPrinter,
} from 'react-icons/fi';
import { useHistory, Redirect } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../utils/init-firebase';
import { groupCRMUsersByDTC } from '../../utils/crmUserSorting';
import CalendarSyncSettings from '../../components/CRM/Settings/CalendarSyncSettings';
import MeetingProviderSettings from '../../components/CRM/Settings/MeetingProviderSettings';
import DialpadSettings from '../../components/CRM/Settings/DialpadSettings';
import AriveSettings from '../../components/CRM/Settings/AriveSettings';
import PostcardManiaSettings from '../../components/CRM/Settings/PostcardManiaSettings';
import SchedulingIntegration from '../../components/CRM/Integrations/SchedulingIntegration';

function IntegrationCard({ integration, isSelected, onSelect }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const selectedBorder = useColorModeValue('blue.400', 'blue.300');
  const border = useColorModeValue('gray.200', 'gray.700');
  const descColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box
      as="button"
      type="button"
      onClick={() => onSelect(integration.key)}
      bg={cardBg}
      borderWidth="2px"
      borderColor={isSelected ? selectedBorder : border}
      borderRadius="xl"
      p={4}
      textAlign="left"
      shadow={isSelected ? 'md' : 'sm'}
      transition="all 0.15s ease"
      _hover={{ shadow: 'md', borderColor: selectedBorder }}
      data-tour={`integration-card-${integration.key}`}
    >
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={integration.icon} boxSize={5} color={`${integration.color}.500`} />
            <Text fontWeight="bold">{integration.name}</Text>
          </HStack>
          <Badge colorScheme={integration.status.color} fontSize="0.65rem">
            {integration.status.label}
          </Badge>
        </HStack>
        <Text fontSize="sm" color={descColor} noOfLines={2}>
          {integration.description}
        </Text>
      </VStack>
    </Box>
  );
}

export default function IntegrationsPage() {
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [crmUsers, setCrmUsers] = useState([]);
  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfUser, setSelfUser] = useState(null);
  const [selfLoaded, setSelfLoaded] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState('scheduling');

  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const effectiveUserId = selectedUserId || currentUser?.uid;
  const isViewingOtherUser = Boolean(selectedUserId && selectedUserId !== currentUser?.uid);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const panelBG = useColorModeValue('white', 'gray.800');

  // Own user doc — for the page gate (calSchedulingEnabled on self)
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      setSelfUser(snap.exists() ? snap.data() : null);
      setSelfLoaded(true);
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // CRM users for the admin "view as" picker
  useEffect(() => {
    if (!isCRMAdmin) return undefined;
    const q = query(collection(db, 'users'), where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const users = [];
      querySnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        users.push({
          uid: userDoc.id,
          name: userData.Name || userData.name || userData.displayName || userData.email,
          email: userData.Email || userData.email,
          ...userData,
        });
      });
      setCrmUsers(users);
    });
    return () => unsubscribe();
  }, [isCRMAdmin]);

  // Target user doc (self or the admin-picked user)
  useEffect(() => {
    if (!effectiveUserId) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, 'users', effectiveUserId),
      (docSnap) => {
        if (docSnap.exists()) {
          setTargetUser({ uid: docSnap.id, ...docSnap.data() });
        } else {
          setTargetUser(null);
        }
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsubscribe();
  }, [effectiveUserId]);

  const integrations = useMemo(() => {
    if (!targetUser) return [];
    const list = [
      {
        key: 'scheduling',
        name: 'FHL Scheduling',
        icon: FiClock,
        color: 'purple',
        description: 'Booking pages, meeting types, availability, and bookings — powered by FHL, sent from you.',
        status: targetUser.bookingHandle
          ? { label: 'Active', color: 'green' }
          : targetUser.calSchedulingEnabled === true
            ? { label: 'Setup needed', color: 'yellow' }
            : { label: 'Not enabled', color: 'gray' },
      },
      {
        key: 'calendar',
        name: 'Google Calendar',
        icon: FiCalendar,
        color: 'blue',
        description: 'Two-way sync between your Google Calendar and the CRM calendar.',
        status:
          targetUser.googleCalendarConnected === true
            ? { label: 'Connected', color: 'green' }
            : { label: 'Not connected', color: 'gray' },
      },
      {
        key: 'meetings',
        name: 'Meeting Providers',
        icon: FiVideo,
        color: 'teal',
        description: 'Default video meeting links (Google Meet, Zoom) for CRM calendar events.',
        status: { label: 'Available', color: 'blue' },
      },
    ];
    if (isCRMAdmin || targetUser.dialpadCapable === true) {
      list.push({
        key: 'dialpad',
        name: 'Dialpad',
        icon: FiPhone,
        color: 'orange',
        description: 'Business texting and calling from the CRM with your Dialpad number.',
        status:
          targetUser.dialpadEnabled === true
            ? { label: 'Enabled', color: 'green' }
            : targetUser.dialpadCapable === true
              ? { label: 'Available', color: 'yellow' }
              : { label: 'Not provisioned', color: 'gray' },
      });
    }
    // Arive LOS temporarily admin-only — hidden for normal users
    if (isCRMAdmin) {
      list.push({
        key: 'arive',
        name: 'Arive LOS',
        icon: FiLink,
        color: 'cyan',
        description: 'Loan pipeline sync and deal linking with the Arive loan origination system.',
        status:
          targetUser.ariveEnabled === true
            ? { label: 'Connected', color: 'green' }
            : { label: 'Not connected', color: 'gray' },
      });
    }
    // PostcardMania is admin-gated: admins always see it (and manage per-user
    // access); other users only when an admin enabled postcards for them.
    if (isCRMAdmin || targetUser.postcardEnabled === true) {
      list.push({
        key: 'postcards',
        name: 'PostcardMania',
        icon: FiPrinter,
        color: 'green',
        description: 'Physical postcards from campaigns and bulk actions — printed and mailed for you.',
        status:
          targetUser.postcardEnabled === true
            ? { label: 'Enabled', color: 'green' }
            : { label: 'Disabled', color: 'gray' },
      });
    }
    return list;
  }, [targetUser, isCRMAdmin]);

  // ----- Gate: pilot users + CRM admins only (after all hooks) -----
  if (!selfLoaded || loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading integrations...</Text>
        </Center>
      </Container>
    );
  }
  const canSeePage =
    isCRMAdmin || selfUser?.calSchedulingEnabled === true || selfUser?.postcardEnabled === true;
  if (!canSeePage) {
    return <Redirect to="/crm/dashboard" />;
  }

  const selectedMeta = integrations.find((i) => i.key === selectedIntegration) || null;

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6} maxW="1400px" mx="auto">
          {/* Header */}
          <Box bg={headerBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3}>
                <Tooltip label="Back to My Deals">
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Back to My Deals"
                    onClick={() => history.push('/crm/my-deals')}
                    size="lg"
                    variant="ghost"
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={1}>
                  <HStack spacing={2}>
                    <Icon as={FiLink} boxSize={6} color="blue.500" />
                    <Heading size="lg">
                      {isViewingOtherUser
                        ? `${targetUser?.Name || targetUser?.name || 'User'}'s Integrations`
                        : 'Integrations'}
                    </Heading>
                  </HStack>
                  <Text color="gray.600" fontSize="sm">
                    Connect and manage the tools that plug into Origin
                  </Text>
                </VStack>
              </HStack>

              {isCRMAdmin && (
                <VStack align="flex-end" spacing={2}>
                  <FormControl maxW="300px">
                    <FormLabel fontSize="xs" mb={1} textTransform="uppercase" fontWeight="semibold" color="gray.600">
                      View Integrations For
                    </FormLabel>
                    <ChakraSelect
                      size="sm"
                      placeholder="My Integrations"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      {(() => {
                        const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                        return (
                          <>
                            {dtcUsers.length > 0 && (
                              <optgroup label="DTC Loan Officers">
                                {dtcUsers.map((user) => (
                                  <option key={user.uid} value={user.uid}>{user.name}</option>
                                ))}
                              </optgroup>
                            )}
                            <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                              {otherUsers.map((user) => (
                                <option key={user.uid} value={user.uid}>{user.name}</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </ChakraSelect>
                  </FormControl>
                  {isViewingOtherUser && (
                    <Badge colorScheme="purple" fontSize="sm">
                      Admin Mode
                    </Badge>
                  )}
                </VStack>
              )}
            </HStack>
          </Box>

          {/* Card grid */}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing={4}>
            {integrations.map((integration) => (
              <IntegrationCard
                key={integration.key}
                integration={integration}
                isSelected={selectedIntegration === integration.key}
                onSelect={setSelectedIntegration}
              />
            ))}
          </SimpleGrid>

          {/* Selected integration panel */}
          {selectedMeta && (
            <Box bg={panelBG} borderRadius="xl" shadow="sm" p={6} data-tour="integration-panel">
              {selectedIntegration === 'scheduling' && (
                <SchedulingIntegration
                  userId={effectiveUserId}
                  targetUser={targetUser}
                  isAdmin={isCRMAdmin}
                />
              )}
              {selectedIntegration === 'calendar' && <CalendarSyncSettings userId={effectiveUserId} />}
              {selectedIntegration === 'meetings' && <MeetingProviderSettings userId={effectiveUserId} />}
              {selectedIntegration === 'dialpad' && (
                <DialpadSettings userId={effectiveUserId} isAdmin={isCRMAdmin} />
              )}
              {selectedIntegration === 'arive' && (
                <AriveSettings userId={effectiveUserId} isAdmin={isCRMAdmin} />
              )}
              {selectedIntegration === 'postcards' && (
                <PostcardManiaSettings userId={effectiveUserId} isAdmin={isCRMAdmin} />
              )}
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
}
